import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Trophy, Users, Heart, Calendar, ArrowRight, Quote, Facebook, Instagram, Download, HandCoins, User, Phone, ShoppingBag } from 'lucide-react';
import { heroImages, galleryImages, testimonials, iceLogo, heroFans, heroVictory, heroChampion, joinTeamImage, executiveBoard, programDirectors, icePatch } from '../mock';
import Countdown from '../components/Countdown';
import NextPractice from '../components/NextPractice';
import AlumniSpotlight from '../components/AlumniSpotlight';
import SeasonCountdown from '../components/SeasonCountdown';
import { VisitorCounter } from '../components/VisitorCounter';
import { Headshot } from '../components/Headshot';
import { ChampionsBanner } from '../components/ChampionsBanner';
import { useToast } from '../hooks/use-toast';

export default function Home() {
  const { toast } = useToast();

  const handleDonate = () => {
    window.open('https://app.autobooks.co/pay/brookline-jamaica-plain', '_blank');
  };

  const handleDownload = (name) => {
    toast({ title: 'Download starting', description: `${name} — opens in new tab (demo).` });
  };

  return (
    <div className="font-body">
      <ChampionsBanner />
      {/* HERO */}
      <section className="relative min-h-[82svh] flex items-center overflow-hidden bg-[#0a1f44]">
        <div className="absolute inset-0">
          <img
            src={heroVictory}
            alt="Founding Patriots coaches"
            className="w-full h-full object-cover object-center animate-kenburns"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1f44]/95 via-[#0a1f44]/70 to-[#0a1f44]/25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44] via-transparent to-transparent" />
          <div className="absolute inset-0 diagonal-stripes opacity-40" />
        </div>

        {/* Floating 2021 Champions plaque — top-right corner, positioned above the coach in the background */}
        <div className="hidden md:block absolute top-24 right-6 lg:right-10 z-10 pointer-events-none" data-testid="hero-champion-card">
          <div className="relative w-[200px] lg:w-[240px]">
            <div className="absolute -top-2 -left-2 w-full h-full border-4 border-[#c8102e]" />
            <img
              src={heroChampion}
              alt="Coach with 2021 National League U8 Division 2 Champions plaque"
              className="relative w-full h-auto object-cover shadow-2xl"
            />
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-1 whitespace-nowrap">
              2021 Champions
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28 pb-12 sm:pb-16 w-full">
          <div className="max-w-3xl">
            <div className="reveal-up flex flex-wrap items-center gap-4 mb-6">
              <img src={iceLogo} alt="ICE Logo" className="h-16 md:h-20 w-auto object-contain drop-shadow-2xl" />
              <div className="inline-flex items-center gap-2 bg-[#c8102e] text-white px-4 py-1.5">
                <Trophy className="w-4 h-4" />
                <span className="font-heading uppercase tracking-[0.25em] text-xs">Est. 2004 • 20 Years Strong</span>
              </div>
            </div>
            <h1 className="reveal-up font-display text-white text-4xl sm:text-5xl md:text-7xl leading-[0.9] mb-4" style={{ animationDelay: '0.1s' }}>
              EXPERIENCE THE
              <br />
              <span className="text-[#c8102e]">THRILL</span> OF
              <br />
              <span className="text-stroke-white">VICTORY</span>
            </h1>
            <p className="reveal-up text-white/85 text-base sm:text-lg max-w-xl mb-8 leading-relaxed" style={{ animationDelay: '0.3s' }}>
              Join &quot;ICE&quot; BROOKLINE JP PATRIOTS Pop-Warner Football & Cheerleading. Discover your potential. Become part of a championship family.
            </p>
            <div className="reveal-up flex flex-wrap gap-4" style={{ animationDelay: '0.5s' }}>
              <Link to="/register" data-testid="hero-register-btn" className="w-full sm:w-auto justify-center group bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase px-6 sm:px-8 py-4 flex items-center gap-2 transition-colors">
                Get Involved <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/apparel"
                data-testid="hero-apparel-btn"
                className="w-full sm:w-auto justify-center group bg-[#0a1f44] hover:bg-[#0d2555] text-white border border-white/30 font-heading uppercase px-6 sm:px-8 py-4 flex items-center gap-2 transition-colors"
              >
                <ShoppingBag className="w-4 h-4" /> Shop Apparel
              </Link>
              <Link to="/about" data-testid="hero-about-btn" className="w-full sm:w-auto text-center border-2 border-white/70 hover:border-white text-white font-heading uppercase px-6 sm:px-8 py-4 transition-colors hover:bg-white hover:text-[#0a1f44]">
                Our Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Next Practice — live banner */}
      <NextPractice />

      {/* In Memory of Ian — compact tribute strip */}
      <section className="bg-[#0a1f44] border-y border-white/10" data-testid="home-tribute-card">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
          <div className="w-16 h-16 border-2 border-[#c8102e] shadow-lg shrink-0 overflow-hidden">
            <img
              src="https://customer-assets-gfyr7b9c.emergentagent.net/job_navy-red-hub/artifacts/wctlpnn3_ed9addf9-8470-44d5-9e7e-aca224fb1a62.jpeg"
              alt="Ian Cotterell"
              className="w-full h-full object-cover"
              style={{ objectPosition: '72% 24%', transform: 'scale(1.55)', transformOrigin: '72% 24%' }}
            />
          </div>
          <div className="flex-1 min-w-0 text-white text-center sm:text-left">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px] mb-1">In Loving Memory</div>
            <div className="font-display text-xl sm:text-2xl leading-tight">
              IAN <span className="text-[#c8102e]">"ICE"</span> COTTERELL · FOUNDER
            </div>
            <div className="text-white/60 text-xs mt-1">A legacy of passion, dedication, and community — carried forward every game.</div>
          </div>
          <Link
            to="/tribute"
            data-testid="home-tribute-cta"
            className="inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest text-[10px] px-5 py-3 whitespace-nowrap transition-colors"
          >
            Read Tribute <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </section>

      {/* Season Countdown */}
      <SeasonCountdown />

      {/* BJP Alumni of the Month spotlight */}
      <AlumniSpotlight />

      {/* Stat strip */}
      <section className="bg-[#0a1f44] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
          {[
            { n: '20+', l: 'YEARS STRONG' },
            { n: '500+', l: 'YOUTH SCHOLARS' },
            { n: '12', l: 'CHAMPIONSHIPS' },
            { n: '6', l: 'TEAMS & SQUADS' }
          ].map((s) => (
            <div key={s.l} className="flex items-baseline gap-3" data-testid={`home-stat-${s.l.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
              <div className="font-display text-3xl md:text-4xl text-[#c8102e]">{s.n}</div>
              <div className="font-heading text-xs tracking-[0.3em] text-white/70">{s.l}</div>
            </div>
          ))}
          <VisitorCounter />
        </div>
      </section>

      <div className="stripe-sep" />

      {/* JOIN */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <div>
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Join Our Program</div>
            <h2 className="font-display text-5xl md:text-6xl text-[#0a1f44] leading-tight mb-6">JOIN THE<br />WINNING TEAM</h2>
            <p className="text-[#0a1f44]/75 text-lg mb-6 leading-relaxed">
              Looking for a fun and competitive way to stay active? Look no further than &quot;ICE&quot; BROOKLINE JP PATRIOTS POP-WARNER FOOTBALL & CHEERLEADING. Our scholars are passionate athletes dedicated to giving their all on & off the field.
            </p>
            <ul className="space-y-3 mb-8">
              {['Teamwork, discipline & respect', 'Championship winning program', 'Ages 5-14 — Football & Cheer', 'Certified Pop Warner coaching staff'].map((f) => (
                <li key={f} className="flex items-center gap-3 text-[#0a1f44]">
                  <span className="w-2 h-2 bg-[#c8102e] rotate-45" /> {f}
                </li>
              ))}
            </ul>
            <Link to="/team" className="inline-flex items-center gap-2 bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest px-8 py-4 transition-colors">
              Find Out More <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-full h-full border-4 border-[#c8102e]" />
            <div className="relative w-full h-[500px] bg-[#0a1f44] flex items-center justify-center overflow-hidden">
              <img src={joinTeamImage} alt="2025 NMPW 7U Division Champions — Join the Winning Team" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-[#0a1f44] text-white p-6 max-w-[220px]">
              <Users className="w-8 h-8 text-[#c8102e] mb-2" />
              <div className="font-display text-3xl">FAMILY</div>
              <div className="font-heading uppercase tracking-widest text-xs text-white/70">More than a team</div>
            </div>
          </div>
        </div>
      </section>

      {/* DONATION + COUNTDOWN */}
      <section className="relative py-24 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0 stars-bg" />
        <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Support the Program</div>
            <h2 className="font-display text-white text-5xl md:text-6xl leading-tight mb-6">
              HELP <span className="text-[#c8102e]">&quot;ICE&quot;</span><br />PATRIOTS THRIVE!
            </h2>
            <p className="text-white/75 text-lg mb-8 leading-relaxed max-w-xl">
              Your contribution will make a huge difference in our ability to compete in tournaments and provide quality training for our team. Every little bit counts!
            </p>
            <div className="flex flex-wrap gap-4">
              <button onClick={handleDonate} data-testid="home-donate-now-btn" className="group inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#e11d34] text-white font-heading uppercase tracking-widest px-10 py-4 transition-colors">
                <Heart className="w-4 h-4" /> Donate Now <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <Link to="/sponsors" data-testid="home-sponsors-btn" className="group inline-flex items-center gap-2 border-2 border-white/70 hover:border-white text-white font-heading uppercase tracking-widest px-10 py-4 transition-colors hover:bg-white hover:text-[#0a1f44]">
                <HandCoins className="w-4 h-4" /> Sponsors &amp; Donation
              </Link>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-6">
              <Calendar className="w-5 h-5 text-[#c8102e]" />
              <div className="font-heading uppercase tracking-[0.3em] text-white/70 text-xs">Countdown to the BBQ</div>
            </div>
            <Countdown />
            <p className="text-white/60 mt-4 text-sm font-heading tracking-wider">Patriots Family Barbecue — Aug 15, 2026 · 2:00 PM · 239 Parker Hill Ave, Boston</p>
          </div>
        </div>
      </section>

      <div className="stripe-sep" />

      {/* EXECUTIVE BOARD */}
      <section className="py-24 bg-[#f5f6f8]" id="executive-board">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Program Leadership</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">EXECUTIVE BOARD</h2>
            <p className="text-[#0a1f44]/70 mt-4">The people who make the &quot;ICE&quot; Brookline Jamaica Plain Patriots run. Reach out anytime.</p>
          </div>
          <div className="grid grid-cols-1 min-[430px]:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6" data-testid="executive-board-grid">
            {executiveBoard.map((m) => (
              <div key={m.role} data-testid={`board-${m.role.toLowerCase().replace(/\s+/g, '-')}`} className="hover-lift bg-white border border-[#0a1f44]/10 overflow-hidden group">
                <div className="relative aspect-[3/4] bg-[#0a1f44] overflow-hidden">
                  {m.img ? (
                    <Headshot name={m.name} src={m.img} className="group-hover:scale-105 transition-transform duration-700" />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-white/40 p-4 text-center">
                      <User className="w-12 h-12 mb-3" />
                      <div className="font-heading uppercase tracking-[0.2em] text-[10px]">Photo coming soon</div>
                    </div>
                  )}
                  <img
                    src={icePatch}
                    alt="ICE Patriots patch"
                    data-testid={`board-patch-${m.role.toLowerCase().replace(/\s+/g, '-')}`}
                    className="absolute top-2 right-2 h-12 w-auto object-contain drop-shadow-lg z-10 pointer-events-none"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44] via-transparent to-transparent opacity-70 pointer-events-none" />
                  <div className="absolute top-4 left-4 bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-1.5 pointer-events-none">{m.role}</div>
                </div>
                <div className="p-5">
                  <div className="font-display text-[#0a1f44] text-xl leading-tight" data-testid={`board-name-${m.role.toLowerCase()}`}>{m.name.toUpperCase()}</div>
                  {m.titles && (
                    <ul className="mt-2 space-y-1" data-testid={`board-titles-${m.role.toLowerCase()}`}>
                      {m.titles.map((t) => (
                        <li key={t} className="flex items-center gap-2 text-[#0a1f44]/80 font-heading uppercase tracking-widest text-[11px]"><span className="w-1.5 h-1.5 bg-[#c8102e] shrink-0" aria-hidden="true" />{t}</li>
                      ))}
                    </ul>
                  )}
                  {m.phone ? (
                    <a href={`tel:${m.phone.replace(/[^\d]/g, '')}`} data-testid={`board-phone-${m.role.toLowerCase()}`} className="mt-3 inline-flex items-center gap-2 text-[#0a1f44]/70 hover:text-[#c8102e] text-sm font-heading tracking-wider transition-colors">
                      <Phone className="w-4 h-4" /> {m.phone}
                    </a>
                  ) : (
                    <div className="mt-3 text-[#0a1f44]/40 text-sm font-heading italic">Contact TBA</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Program Directors — separate row below Executive Board */}
          <div className="mt-14" data-testid="program-directors-row">
            <div className="flex items-center gap-4 mb-6 max-w-md mx-auto">
              <div className="h-0.5 flex-1 bg-[#0a1f44]/10" />
              <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs whitespace-nowrap">Program Directors</div>
              <div className="h-0.5 flex-1 bg-[#0a1f44]/10" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
              {programDirectors.map((d) => (
                <div key={d.role} data-testid={`director-card-${d.name.replace(/\s+/g, '-').toLowerCase()}`} className="hover-lift bg-white border border-[#0a1f44]/10 overflow-hidden group">
                  <div className="relative aspect-[3/4] bg-[#0a1f44] overflow-hidden">
                    <Headshot name={d.name} src={d.img} className="group-hover:scale-105 transition-transform duration-700" />
                    <img
                      src={icePatch}
                      alt="ICE Patriots patch"
                      data-testid={`director-patch-${d.name.replace(/\s+/g, '-').toLowerCase()}`}
                      className="absolute top-2 right-2 h-12 w-auto object-contain drop-shadow-lg z-10 pointer-events-none"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f44] via-transparent to-transparent opacity-70 pointer-events-none" />
                    <div className="absolute top-4 left-4 bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-3 py-1.5 pointer-events-none">{d.role}</div>
                  </div>
                  <div className="p-5">
                    <div className="font-display text-[#0a1f44] text-xl leading-tight" data-testid={`director-name-${d.name.replace(/\s+/g, '-').toLowerCase()}`}>{d.name.toUpperCase()}</div>
                    <a href={`tel:${d.phone.replace(/[^\d]/g, '')}`} data-testid={`director-phone-${d.name.replace(/\s+/g, '-').toLowerCase()}`} className="mt-3 inline-flex items-center gap-2 text-[#0a1f44]/70 hover:text-[#c8102e] text-sm font-heading tracking-wider transition-colors">
                      <Phone className="w-4 h-4" /> {d.phone}
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Fans Speak Out</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">LET&apos;S GO PATRIOTS!</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div key={i} className="hover-lift bg-white border-2 border-[#0a1f44]/10 p-8 relative">
                <Quote className="w-10 h-10 text-[#c8102e]/20 absolute top-6 right-6" />
                <p className="text-[#0a1f44]/80 leading-relaxed mb-6 italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="border-t border-[#0a1f44]/10 pt-4">
                  <div className="font-heading tracking-widest text-[#0a1f44] font-semibold">{t.name}</div>
                  <div className="text-xs uppercase tracking-widest text-[#c8102e] mt-1">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORMS */}
      <section className="py-24 bg-[#f5f6f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Get Ready</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl mb-4">FORMS</h2>
            <p className="text-[#0a1f44]/70">Please do not double side the application. Double sided applications will not be accepted. Thank you.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
            {[
              { name: 'BJP 2026 REGISTRATION PACKET', size: '2.4 MB' },
              { name: 'BJP 2026 CORI FORM', size: '820 KB' }
            ].map((f) => (
              <div key={f.name} className="hover-lift bg-white border-2 border-[#0a1f44]/10 p-5 sm:p-6 flex flex-col min-[400px]:flex-row min-[400px]:items-center gap-4">
                <div className="w-14 h-14 bg-[#c8102e] text-white flex items-center justify-center flex-shrink-0">
                  <Download className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="font-heading text-[#0a1f44] font-semibold">{f.name}</div>
                  <div className="text-xs text-[#0a1f44]/60 mt-1">PDF • {f.size}</div>
                </div>
                <button onClick={() => handleDownload(f.name)} data-testid={`home-form-download-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} className="w-full min-[400px]:w-auto bg-[#0a1f44] hover:bg-[#c8102e] text-white font-heading uppercase text-xs px-4 py-2 transition-colors">
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Photo Gallery</div>
            <h2 className="font-display text-[#0a1f44] text-4xl md:text-5xl leading-tight">PATRIOTS MOMENTS: A PHOTO EXHIBITION<br />OF OUR DEDICATION AND TEAMWORK</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {galleryImages.map((src, i) => (
              <div key={i} className="overflow-hidden">
                <img src={src} alt={`gallery ${i}`} className="gallery-img w-full h-full object-cover aspect-square" loading="lazy" decoding="async" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONNECT */}
      <section className="py-20 bg-[#0a1f44] relative overflow-hidden">
        <div className="absolute inset-0 stars-bg" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-white text-4xl md:text-5xl mb-8">CONNECT WITH US</h2>
          <div className="flex justify-center gap-4">
            <a href="https://www.facebook.com/627656147646539" target="_blank" rel="noreferrer" data-testid="home-facebook-link" aria-label="Brookline JP Patriots on Facebook" className="group w-14 h-14 sm:w-16 sm:h-16 border-2 border-white/20 hover:border-[#c8102e] hover:bg-[#c8102e] flex items-center justify-center transition-[background-color,border-color]">
              <Facebook className="w-6 h-6 text-white" />
            </a>
            <a href="https://www.instagram.com/brooklinejppats" target="_blank" rel="noreferrer" data-testid="home-instagram-link" aria-label="Brookline JP Patriots on Instagram" className="group w-14 h-14 sm:w-16 sm:h-16 border-2 border-white/20 hover:border-[#c8102e] hover:bg-[#c8102e] flex items-center justify-center transition-[background-color,border-color]">
              <Instagram className="w-6 h-6 text-white" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
